/**
 * Who tagged us on Instagram.
 *
 * One document per tagged post in `skidaCrmMentions`, keyed by the media id so
 * a post cannot be recorded twice however often the collector runs.
 *
 * ## Exact handle matching only
 *
 * Attribution is a string equality test against the Instagram handles stored on
 * ambassadors, partners and leads. Nothing fuzzy.
 *
 * That rule was written after watching fuzzy matching get it wrong on real
 * data: `@wanakamountainguides` matched a lead called "Mountain Guide" on a
 * substring, while the company's actual record — "Wanaka Mountain Guides" —
 * has no handle stored and so was invisible. Fuzzy matching produced a
 * confident wrong answer where the right one was simply missing. A false
 * attribution is worse than none, because nobody re-checks a row that looks
 * plausible.
 *
 * ## The handle belongs to whoever holds it
 *
 * `@wanakamountainguides` is a company account, but it is recorded on Tim
 * Steward, who is an ambassador and runs the company. So the credit goes to
 * him. There is no attempt to be cleverer than that: one handle, one holder.
 * If two records claim the same handle the collector records the conflict
 * rather than picking a winner, because picking one silently would make the
 * other permanently invisible.
 */

export type MentionMatchKind = 'ambassador' | 'partner' | 'lead' | 'event'

export interface Mention {
  /** Instagram media id, and the document id. */
  id: string

  /** The account that posted it, without the @. */
  username: string
  /** ISO timestamp of the post. */
  postedAt: string | null
  permalink: string
  caption: string
  mediaType: string
  /** REELS, FEED or STORY — a reel at 40k is a different event from a post at 400. */
  productType: string
  /**
   * Instagram's CDN link, refreshed on every collector run.
   *
   * Signed and short-lived: these expire within days, so the image must be
   * allowed to fail without taking the card with it. Stored rather than
   * proxied because proxying would mean serving somebody else's media from our
   * own domain, which is a copyright question we have no reason to open.
   */
  thumbnailUrl: string
  likeCount: number | null
  commentCount: number | null

  /**
   * Who this was attributed to, or null when the handle matches nothing we
   * hold. Unmatched is a normal state and a piece of work — somebody either
   * records the handle on an existing record, or decides it is a stranger.
   */
  matchKind: MentionMatchKind | null
  matchId: string
  matchName: string

  /**
   * Set when more than one record claims this handle. Both names are kept so
   * the conflict can be resolved rather than guessed at.
   */
  conflict: string

  /** When the collector first saw it. */
  seenAt: string | null
}

export const MATCH_LABEL: Record<MentionMatchKind, string> = {
  ambassador: 'Ambassador',
  partner: 'Partner',
  lead: 'Lead',
  event: 'Event',
}
